/**
 * LakehouseEditModal Component
 *
 * Modal for editing lakehouse protection tier and compliance tags (FR-203, FR-204).
 * Shows current configuration and allows updates with validation.
 *
 * Requirements:
 * - FR-203: Assign protection tier
 * - FR-204: Update compliance tags
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LakehouseWithWorkspace, useUpdateComplianceTags } from '../../hooks/useLakehouses';
import { AppTooltip, Select } from '@/components/ui';
import { lakehouseSizeNote } from '../common/LakehouseSizeCell';
import { tiersApi } from '../../api/tiers';
import { retentionApi } from '../../api/retention';
import { assignmentApi } from '../../api/assignment';
import { tierIcon } from '../common/ProtectionTierBadge';
import { ComplianceTagChips, PREDEFINED_TAGS } from '../common/ComplianceTagChips';
import { useDraggable } from '../../../../components/ui/useDraggable';
import { useBackupActivity } from '../../store/backupActivityStore';
import { formatEastern } from '@/utils/dateFormatter';

interface LakehouseEditModalProps {
  lakehouse: LakehouseWithWorkspace | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

type EditTab = 'tier' | 'compliance' | 'details';

export const LakehouseEditModal: React.FC<LakehouseEditModalProps> = ({
  lakehouse,
  isOpen,
  onClose,
  onSaved,
}) => {
  const [activeTab, setActiveTab] = useState<EditTab>('tier');
  const [selectedTier, setSelectedTier] = useState<string>('Bronze');
  const [selectedRetention, setSelectedRetention] = useState<string>(''); // '' = inherit the default
  const [retentionTouched, setRetentionTouched] = useState(false);
  const [complianceTags, setComplianceTags] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savingSla, setSavingSla] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const qc = useQueryClient();
  const updateTagsMutation = useUpdateComplianceTags();
  const { transform, onHandleMouseDown } = useDraggable();
  const recordActivity = useBackupActivity((s) => s.add);

  // The 5 settings-managed tiers (real RPO/RTO) + retention policies — the single source of truth.
  const tiersQ = useQuery({ queryKey: ['backup', 'tiers'], queryFn: tiersApi.list });
  const policiesQ = useQuery({ queryKey: ['backup', 'retention-policies'], queryFn: retentionApi.listPolicies });
  const tierRpo = (name: string): string => {
    const t = tiersQ.data?.find((x) => x.tierName === name);
    if (!t || t.rpoHours == null) return '';
    const h = t.rpoHours;
    return h >= 24 && h % 24 === 0 ? `${h / 24}d RPO` : `${h}h RPO`;
  };

  // Reset state when lakehouse changes
  useEffect(() => {
    if (lakehouse) {
      setSelectedTier(lakehouse.protectionTier);
      setSelectedRetention('');
      setRetentionTouched(false);
      setComplianceTags(lakehouse.complianceTags || []);
      setHasChanges(false);
      setSaveError(null);
      setConfirming(false);
    }
  }, [lakehouse]);

  // A tier change is the recovery objective changing → when backup is enabled, it triggers a full
  // backup. Confirm first (warn of the consequence). Not editable on the Backups screen — only here.
  const tierChanged = !!lakehouse && selectedTier !== lakehouse.protectionTier;
  const backupEnabled = !!lakehouse?.enabled;
  const willTriggerBackup = tierChanged && backupEnabled;

  // Track changes
  useEffect(() => {
    if (!lakehouse) return;

    const tierChanged = selectedTier !== lakehouse.protectionTier;
    const tagsChanged =
      JSON.stringify([...complianceTags].sort()) !==
      JSON.stringify([...(lakehouse.complianceTags || [])].sort());

    setHasChanges(tierChanged || tagsChanged || retentionTouched);
  }, [lakehouse, selectedTier, complianceTags, retentionTouched]);

  const handleSave = useCallback(async () => {
    if (!lakehouse || !hasChanges) return;
    // First click on a tier change (with backup enabled) → show the consequence confirmation.
    if (willTriggerBackup && !confirming) {
      setConfirming(true);
      return;
    }

    setSaveError(null);
    setSavingSla(true);

    try {
      const promises: Promise<unknown>[] = [];

      // Tier + retention via the new source-level endpoints (5 settings-managed tiers, both types).
      if (selectedTier !== lakehouse.protectionTier) {
        promises.push(tiersApi.setEntityTier('lakehouse', lakehouse.id, selectedTier));
      }
      if (retentionTouched) {
        promises.push(retentionApi.setEntityRetention('lakehouse', lakehouse.id, selectedRetention || null));
      }

      // Check for compliance tags change
      const tagsChanged =
        JSON.stringify([...complianceTags].sort()) !==
        JSON.stringify([...(lakehouse.complianceTags || [])].sort());

      if (tagsChanged) {
        promises.push(
          updateTagsMutation.mutateAsync({
            id: lakehouse.id,
            tags: complianceTags,
          })
        );
      }

      await Promise.all(promises);
      // A tier change immediately triggers a full backup (only when backup is enabled).
      // Record it so the global activity drawer pops open with live progress (FR-040).
      if (willTriggerBackup) {
        const r = await assignmentApi
          .triggerBackup('lakehouse', lakehouse.id, 'full')
          .catch(() => undefined);
        if (r?.launched && r.executionName) {
          recordActivity({
            executionName: r.executionName,
            verb: 'Backup',
            subject: lakehouse.lakehouseName,
            entityType: 'lakehouse',
            entityId: lakehouse.id,
          });
        }
      }
      // Refresh the catalog + backup readiness so the new tier/retention show immediately.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['lakehouses'] }),
        qc.invalidateQueries({ queryKey: ['backup', 'backup-configs'] }),
        qc.invalidateQueries({ queryKey: ['backup', 'dash', 'readiness'] }),
      ]);
      onSaved?.();
      onClose();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save changes');
    } finally {
      setSavingSla(false);
    }
  }, [
    willTriggerBackup,
    confirming,
    lakehouse,
    hasChanges,
    selectedTier,
    selectedRetention,
    retentionTouched,
    complianceTags,
    updateTagsMutation,
    qc,
    onSaved,
    onClose,
    recordActivity,
  ]);

  const handleAddTag = useCallback(
    (tag: string) => {
      if (!complianceTags.includes(tag)) {
        setComplianceTags([...complianceTags, tag]);
      }
    },
    [complianceTags]
  );

  const handleRemoveTag = useCallback(
    (tag: string) => {
      setComplianceTags(complianceTags.filter((t) => t !== tag));
    },
    [complianceTags]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  if (!isOpen || !lakehouse) return null;

  const isSaving = savingSla || updateTagsMutation.isPending;

  const formatDate = (dateStr?: string | null): string => {
    if (!dateStr) return 'Never';
    return formatEastern(dateStr);
  };

  const formatSize = (sizeGb?: number | null): string => {
    if (!sizeGb) return '-';
    if (sizeGb < 1) return `${Math.round(sizeGb * 1024)} MB`;
    if (sizeGb >= 1024) return `${(sizeGb / 1024).toFixed(1)} TB`;
    return `${sizeGb.toFixed(1)} GB`;
  };

  return (
    <div
      className='fixed inset-0 z-50 overflow-y-auto'
      aria-labelledby='modal-title'
      role='dialog'
      aria-modal='true'
      onKeyDown={handleKeyDown}
    >
      {/* Backdrop — transparent + non-dismissing (Spec 197) */}
      <div
        className='fixed inset-0 bg-transparent transition-opacity'
        aria-hidden='true'
      />

      {/* Modal */}
      <div className='flex min-h-full items-center justify-center p-4'>
        <div className='relative w-full max-w-2xl rounded-xl bg-[var(--color-bg-card)] border border-[var(--color-border)] shadow-2xl transition-all' style={{ transform }}>
          {/* Header (drag handle) */}
          <div
            onMouseDown={onHandleMouseDown}
            className='flex items-center justify-between border-b border-[var(--color-border)] px-6 py-4 cursor-move select-none'
          >
            <div>
              <h2
                id='modal-title'
                className='text-lg font-semibold text-[var(--color-text-primary)]'
              >
                Edit Lakehouse
              </h2>
              <p className='text-sm text-[var(--color-text-secondary)]'>{lakehouse.lakehouseName}</p>
            </div>
            <button
              type='button'
              onClick={onClose}
              className='text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
            >
              <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className='border-b border-[var(--color-border)]'>
            <nav className='flex px-6 -mb-px' aria-label='Tabs'>
              {[
                { id: 'tier', label: 'Protection Tier' },
                { id: 'compliance', label: 'Compliance Tags' },
                { id: 'details', label: 'Details' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type='button'
                  onClick={() => setActiveTab(tab.id as EditTab)}
                  className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors
                    ${
                      activeTab === tab.id
                        ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                        : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border)]'
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className='px-6 py-6 max-h-[60vh] overflow-y-auto'>
            {confirming && (
              <div className='mb-4 flex items-start gap-3 rounded-md border border-[var(--color-status-warning)] bg-[var(--color-bg-subtle)] px-4 py-3'>
                <span className='mt-0.5 text-lg'>⚠️</span>
                <div className='text-sm'>
                  <p className='font-medium text-[var(--color-text-primary)]'>
                    Changing the protection tier to <strong>{selectedTier}</strong> will immediately start a{' '}
                    <strong>full backup</strong> of <strong>{lakehouse.lakehouseName}</strong>.
                  </p>
                  <p className='mt-1 text-[var(--color-text-secondary)]'>
                    A full backup can take a while and consume storage. The new tier&apos;s objective (RPO/RTO) applies once it
                    completes. Click &quot;Confirm &amp; back up now&quot; to proceed.
                  </p>
                </div>
              </div>
            )}
            {/* Protection Tier Tab */}
            {activeTab === 'tier' && (
              <div className='space-y-6'>
                <div>
                  <label className='block text-sm font-medium text-[var(--color-text-secondary)] mb-2'>
                    Protection tier (recovery objective)
                  </label>
                  <Select
                    value={selectedTier}
                    onChange={(e) => setSelectedTier(e.target.value)}
                    options={(tiersQ.data ?? []).map((t) => ({
                      value: t.tierName,
                      label: `${tierIcon(t.tierName)} ${t.tierName}${tierRpo(t.tierName) ? ` · ${tierRpo(t.tierName)}` : ''}`,
                    }))}
                  />
                  <p className='mt-1 text-xs text-[var(--color-text-secondary)]'>
                    Tiers + their RPO/RTO are defined in Backup → Settings.
                  </p>
                  {willTriggerBackup && !confirming && (
                    <p className='mt-1 text-xs text-[var(--color-status-warning)]'>
                      ⚠️ Saving a tier change immediately triggers a full backup.
                    </p>
                  )}
                  {tierChanged && !backupEnabled && (
                    <p className='mt-1 text-xs text-[var(--color-text-secondary)]'>
                      Backup isn&apos;t enabled for this lakehouse — no backup will run.
                    </p>
                  )}
                </div>
                <div>
                  <label className='block text-sm font-medium text-[var(--color-text-secondary)] mb-2'>
                    Retention policy
                  </label>
                  <Select
                    value={selectedRetention}
                    onChange={(e) => {
                      setSelectedRetention(e.target.value);
                      setRetentionTouched(true);
                    }}
                    options={[
                      { value: '', label: 'Inherit default' },
                      ...(policiesQ.data ?? [])
                        .filter((p) => !p.appliesToEntityType || p.appliesToEntityType === 'lakehouse')
                        .map((p) => ({ value: p.id, label: p.name })),
                    ]}
                  />
                  <p className='mt-1 text-xs text-[var(--color-text-secondary)]'>
                    How long copies are kept (GFS). Leave on Inherit to use the default for lakehouses.
                  </p>
                </div>

                {/* Tier Info */}
                <div className='bg-[var(--color-bg-subtle)] rounded-lg p-4'>
                  <h4 className='text-sm font-medium text-[var(--color-text-secondary)] mb-2'>
                    Current Configuration
                  </h4>
                  <dl className='grid grid-cols-2 gap-4 text-sm'>
                    <div>
                      <dt className='text-[var(--color-text-secondary)]'>Current Tier</dt>
                      <dd className='font-medium text-[var(--color-text-primary)]'>
                        {lakehouse.protectionTier}
                      </dd>
                    </div>
                    <div>
                      <dt className='text-[var(--color-text-secondary)]'>Source</dt>
                      <dd className='font-medium text-[var(--color-text-primary)]'>
                        {lakehouse.protectionTierSource}
                      </dd>
                    </div>
                    <div>
                      <dt className='text-[var(--color-text-secondary)]'>Offsite Enabled</dt>
                      <dd className='font-medium text-[var(--color-text-primary)]'>
                        {lakehouse.offsiteEnabled ? 'Yes' : 'No'}
                      </dd>
                    </div>
                    <div>
                      <dt className='text-[var(--color-text-secondary)]'>Archive Enabled</dt>
                      <dd className='font-medium text-[var(--color-text-primary)]'>
                        {lakehouse.archiveEnabled ? 'Yes' : 'No'}
                      </dd>
                    </div>
                  </dl>
                </div>

                {lakehouse.reviewRequired && (
                  <div className='bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4'>
                    <div className='flex items-start gap-3'>
                      <span className='text-yellow-500 text-lg'>⚠️</span>
                      <div>
                        <h4 className='text-sm font-medium text-yellow-800 dark:text-yellow-300'>
                          Review Required
                        </h4>
                        <p className='text-sm text-yellow-700 dark:text-yellow-400 mt-1'>
                          This lakehouse was auto-classified. Please review and confirm the
                          protection tier.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Compliance Tags Tab */}
            {activeTab === 'compliance' && (
              <div className='space-y-6'>
                <div>
                  <label className='block text-sm font-medium text-[var(--color-text-secondary)] mb-2'>
                    Current Tags
                  </label>
                  <ComplianceTagChips
                    tags={complianceTags}
                    editable
                    onRemove={handleRemoveTag}
                    size='md'
                  />
                  {complianceTags.length === 0 && (
                    <p className='text-sm text-[var(--color-text-secondary)] mt-2'>
                      No compliance tags assigned.
                    </p>
                  )}
                </div>

                <div>
                  <label className='block text-sm font-medium text-[var(--color-text-secondary)] mb-2'>
                    Add Tags
                  </label>
                  <div className='flex flex-wrap gap-2'>
                    {PREDEFINED_TAGS.filter((tag) => !complianceTags.includes(tag)).map((tag) => (
                      <button
                        key={tag}
                        type='button'
                        onClick={() => handleAddTag(tag)}
                        className='inline-flex items-center px-3 py-1.5 border border-dashed
                                 border-[var(--color-border)] rounded-full text-sm
                                 text-[var(--color-text-secondary)] hover:border-[var(--color-accent)]
                                 hover:text-[var(--color-accent)] transition-colors'
                      >
                        + {tag}
                      </button>
                    ))}
                  </div>
                </div>

                <div className='bg-[var(--color-bg-subtle)] rounded-lg p-4'>
                  <h4 className='text-sm font-medium text-[var(--color-text-secondary)] mb-2'>
                    Tag Source
                  </h4>
                  <p className='text-sm text-[var(--color-text-secondary)]'>
                    {lakehouse.complianceTagSource === 'Auto-Detected'
                      ? 'Tags were auto-detected based on lakehouse content.'
                      : 'Tags were manually assigned.'}
                  </p>
                </div>
              </div>
            )}

            {/* Details Tab */}
            {activeTab === 'details' && (
              <div className='space-y-4'>
                <dl className='grid grid-cols-2 gap-x-6 gap-y-4'>
                  <div>
                    <dt className='text-sm text-[var(--color-text-secondary)]'>Lakehouse ID</dt>
                    <dd className='mt-1 text-sm font-medium text-[var(--color-text-primary)] font-mono'>
                      {lakehouse.id.slice(0, 8)}...
                    </dd>
                  </div>
                  <div>
                    <dt className='text-sm text-[var(--color-text-secondary)]'>Fabric ID</dt>
                    <dd className='mt-1 text-sm font-medium text-[var(--color-text-primary)] font-mono'>
                      {lakehouse.fabricLakehouseId?.slice(0, 8) || '-'}...
                    </dd>
                  </div>
                  <div>
                    <dt className='text-sm text-[var(--color-text-secondary)]'>Workspace</dt>
                    <dd className='mt-1 text-sm font-medium text-[var(--color-text-primary)]'>
                      {lakehouse.workspaceDisplayName}
                    </dd>
                  </div>
                  <div>
                    <dt className='text-sm text-[var(--color-text-secondary)]'>Environment</dt>
                    <dd className='mt-1 text-sm font-medium text-[var(--color-text-primary)]'>
                      {lakehouse.environment === 'prod' ? 'Production' : 'Non-Production'}
                    </dd>
                  </div>
                  <div>
                    <dt className='text-sm text-[var(--color-text-secondary)]'>Size</dt>
                    <dd className='mt-1 text-sm font-medium text-[var(--color-text-primary)]'>
                      {/* Spec 286 T159 item 32: not sized by the last collection, the stored size is an old one. */}
                      {lakehouse.sizeMeasurementError ? (
                        <AppTooltip title={lakehouseSizeNote(lakehouse.sizeMeasurementError, lakehouse.sizeLastRecordedGb, lakehouse.sizeLastRecordedOn)}>
                          <span className='font-normal text-[var(--color-text-secondary)]'>Not measured</span>
                        </AppTooltip>
                      ) : (
                        formatSize(lakehouse.sizeGb)
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className='text-sm text-[var(--color-text-secondary)]'>Table Count</dt>
                    <dd className='mt-1 text-sm font-medium text-[var(--color-text-primary)]'>
                      {lakehouse.tableCount ?? '-'}
                    </dd>
                  </div>
                  <div>
                    <dt className='text-sm text-[var(--color-text-secondary)]'>Last Backup</dt>
                    <dd className='mt-1 text-sm font-medium text-[var(--color-text-primary)]'>
                      {formatDate(lakehouse.lastBackupAt)}
                    </dd>
                  </div>
                  <div>
                    <dt className='text-sm text-[var(--color-text-secondary)]'>Created</dt>
                    <dd className='mt-1 text-sm font-medium text-[var(--color-text-primary)]'>
                      {formatDate(lakehouse.createdAt)}
                    </dd>
                  </div>
                  <div>
                    <dt className='text-sm text-[var(--color-text-secondary)]'>Backup Status</dt>
                    <dd className='mt-1 flex items-center gap-2'>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
                          ${
                            lakehouse.enabled
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                          }`}
                      >
                        {lakehouse.enabled ? 'Protected' : 'Not protected'}
                      </span>
                      {/* Spec 201: backup is governed centrally — manage in the Backup section. */}
                      <a href='/backup/storage' className='text-xs text-[var(--color-accent)] hover:underline'>
                        Manage in Backup →
                      </a>
                    </dd>
                  </div>
                  <div>
                    <dt className='text-sm text-[var(--color-text-secondary)]'>Backup plan</dt>
                    <dd className='mt-1 text-sm font-medium text-[var(--color-text-primary)]'>
                      {lakehouse.policyName || 'Governed via Backup Plans'}
                    </dd>
                  </div>
                </dl>

                {lakehouse.description && (
                  <div className='pt-4 border-t border-[var(--color-border)]'>
                    <dt className='text-sm text-[var(--color-text-secondary)] mb-1'>Description</dt>
                    <dd className='text-sm text-[var(--color-text-primary)]'>
                      {lakehouse.description}
                    </dd>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Error Message */}
          {saveError && (
            <div className='px-6 pb-4'>
              <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3'>
                <p className='text-sm text-red-700 dark:text-red-400'>{saveError}</p>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className='flex items-center justify-end gap-3 border-t border-[var(--color-border)] px-6 py-4'>
            <button
              type='button'
              onClick={confirming ? () => setConfirming(false) : onClose}
              disabled={isSaving}
              className='px-4 py-2 text-sm font-medium text-[var(--color-text-primary)]
                       bg-[var(--color-bg-card)] border border-[var(--color-border)]
                       rounded-lg hover:bg-[var(--color-bg-subtle)]
                       disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
            >
              {confirming ? 'Back' : 'Cancel'}
            </button>
            <button
              type='button'
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className='px-4 py-2 text-sm font-medium text-white bg-[var(--color-accent)] rounded-lg
                       hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed
                       transition-colors flex items-center gap-2'
            >
              {isSaving && (
                <svg className='animate-spin w-4 h-4' fill='none' viewBox='0 0 24 24'>
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  />
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                  />
                </svg>
              )}
              {isSaving ? 'Saving...' : confirming ? 'Confirm & back up now' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LakehouseEditModal;
